import { useRef, useState } from "react";
import { toast } from "sonner";
import { FolderOpen, MapPin } from "@phosphor-icons/react";
import { injectExif, readGpsAndTime } from "../lib/exif";
import { uploadChunked } from "../lib/uploader";
import { LocalStore, buildFilename } from "../lib/storage";

const MAX_BYTES = 12 * 1024 * 1024; // base64-in-Mongo ceiling (16 MB BSON)

const readAsDataUrl = (file) =>
  new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });

// Import JPEGs from an SD card / drone into the active session: sequence-named,
// session tags baked into EXIF (existing camera EXIF + GPS preserved).
export function ImportPhotos({ session, onDone }) {
  const inputRef = useRef(null);
  const [progress, setProgress] = useState(null); // {done,total,gps}

  const run = async (files) => {
    const list = Array.from(files).filter((f) => /image\/jpe?g/.test(f.type) || /\.jpe?g$/i.test(f.name));
    if (!list.length) return toast.error("ONLY JPEG FILES CAN BE IMPORTED");
    list.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
    let seq = session.current_sequence || 1;
    let gpsCount = 0, failed = 0, imported = 0;
    setProgress({ done: 0, total: list.length, gps: 0 });
    for (const file of list) {
      if (file.size > MAX_BYTES) { failed++; toast.error(`${file.name}: over 12 MB, skipped`); continue; }
      try {
        const raw = await readAsDataUrl(file);
        const { gps, capturedAt, make, model } = readGpsAndTime(raw);
        const filename = buildFilename(session.name, session.base_tags, seq);
        const withExif = injectExif(raw, { sessionName: session.name, tags: session.base_tags, sequence: seq, filename, capturedAt, source: "import" }, { preserve: true });
        await uploadChunked({
          sessionId: session.id, filename, sequence: seq, tags: session.base_tags, dataUrl: withExif,
          metadata: { source: "import", original_filename: file.name, captured_at: capturedAt || new Date(file.lastModified).toISOString(),
                      gps: gps || undefined, camera: [make, model].filter(Boolean).join(" ") || undefined, size_bytes: file.size },
        });
        if (gps) gpsCount++;
        seq++; imported++;
        LocalStore.setActiveSession({ ...session, current_sequence: seq, photo_count: (session.photo_count || 0) + imported });
      } catch (e) {
        failed++;
      }
      setProgress((p) => ({ ...p, done: p.done + 1, gps: gpsCount }));
    }
    setProgress(null);
    toast.success(`IMPORTED ${list.length - failed} · ${gpsCount} WITH GPS${failed ? ` · ${failed} FAILED` : ""}`);
    onDone?.();
  };

  return (
    <div className="inline-flex items-center gap-2" data-testid="import-photos">
      <input ref={inputRef} type="file" accept="image/jpeg,.jpg,.jpeg" multiple className="hidden" data-testid="import-photos-input"
             onChange={(e) => { if (e.target.files?.length) run(e.target.files); e.target.value = ""; }} />
      <button onClick={() => inputRef.current?.click()} disabled={!!progress} className="btn-flat-outline" data-testid="import-photos-btn">
        <FolderOpen weight="bold" size={18} />
        {progress ? `IMPORTING ${progress.done}/${progress.total}` : "Import SD / Drone"}
      </button>
      {progress && progress.gps > 0 && (
        <span className="label-mono text-[10px] text-[#002FA7] inline-flex items-center gap-1" data-testid="import-gps-count">
          <MapPin size={12} weight="bold" /> {progress.gps} GPS
        </span>
      )}
    </div>
  );
}
